/**
 * Client-side gradebook math (issue #13) - mirrors the backend's
 * `services/grades.py` for instant preview before a score/weight edit is saved.
 * The server response remains the source of truth once persisted; this is a
 * live preview only. Matches demo/assets/seed.js's weighted()/letter().
 */
import type { GradeItem } from "@/lib/api/gradebook";
import type { GradeBand } from "@/lib/api/gradeScale";

function scoreForItem(item: GradeItem, scores: Record<string, number | null>): number | null {
  if (item.id in scores) return scores[item.id];
  if (item.key && item.key in scores) return scores[item.key];
  return null;
}

/** Weight-renormalized mean over non-null items; null if none are recorded. */
export function weighted(items: GradeItem[], scores: Record<string, number | null>): number | null {
  let sum = 0;
  let weightSum = 0;
  for (const item of items) {
    const score = scoreForItem(item, scores);
    if (score != null) {
      sum += score * item.weight;
      weightSum += item.weight;
    }
  }
  return weightSum ? Math.round(sum / weightSum) : null;
}

/** Highest band whose min_score <= score; null for no recorded or scaled result. */
export function letter(score: number | null, bands: GradeBand[]): string | null {
  if (score == null) return null;
  let best: GradeBand | null = null;
  for (const band of bands) {
    if (score >= band.min_score && (best == null || band.min_score > best.min_score)) {
      best = band;
    }
  }
  return best?.letter ?? null;
}
